import { LoadingState, Logo, useLocale } from '@nolens/ui';
import { useEffect, useRef, useState } from 'react';
import { Link, Navigate, useLocation } from 'react-router';
import { CONTACT_EMAIL, mailto } from '../lib/contact';
import { useSession } from '../lib/session';
import { landingIcons, landingText, publicPaths, type LandingText } from '../i18n/landing';

/** Mostra a página do produto a quem não tem sessão; quem tem sessão vai para o Início («/inicio»). */
export function HomePage() {
  const { session, loading } = useSession();
  const { search, hash } = useLocation();
  if (loading) return <LoadingState />;
  if (session) return <Navigate to={`/inicio${search}${hash}`} replace />;
  return <LandingPage />;
}

function Cta({
  to,
  children,
  primary,
}: {
  to: string;
  children: React.ReactNode;
  primary?: boolean;
}) {
  return (
    <Link
      to={to}
      className={
        primary
          ? 'inline-flex h-11 items-center justify-center rounded-full bg-accent px-5 text-[15px] font-semibold text-on-accent hover:brightness-95'
          : 'inline-flex h-11 items-center justify-center rounded-full px-5 text-[15px] font-semibold text-text-1 shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-2'
      }
    >
      {children}
    </Link>
  );
}

const card = 'rounded-[14px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border)]';

/** Barra de uma janela do navegador, por cima das imagens de exemplo. */
function WindowBar({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 border-b border-border px-4 py-2.5">
      <span className="size-2.5 rounded-full bg-border-strong" />
      <span className="size-2.5 rounded-full bg-border-strong" />
      <span className="size-2.5 rounded-full bg-border-strong" />
      {children && <span className="ml-3 truncate text-[12.5px] text-text-3">{children}</span>}
    </div>
  );
}

/** Linhas cinzentas: o conteúdo de uma aplicação, esboçado. */
function Lines({ widths }: { widths: number[] }) {
  return (
    <div className="grid gap-2.5">
      {widths.map((w, i) => (
        <span key={i} className="h-2 rounded-full bg-border" style={{ width: `${w}%` }} />
      ))}
    </div>
  );
}

/** A extensão a gravar: a aplicação à esquerda, a barra lateral com os passos à direita. */
function HeroDemo({ t }: { t: LandingText }) {
  return (
    <figure
      aria-label={t.demoLabel}
      className="overflow-hidden rounded-[20px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border),0_24px_48px_-24px_rgb(0_0_0/0.25)]"
    >
      <WindowBar>{t.demoHost}</WindowBar>
      <div className="grid grid-cols-[minmax(0,1fr)_minmax(0,160px)] sm:grid-cols-[minmax(0,1fr)_minmax(0,200px)]">
        <div className="grid min-w-0 content-start gap-5 overflow-hidden bg-surface-0 p-3 sm:p-4">
          <div className="flex items-center justify-end gap-3 sm:justify-between">
            <span className="hidden h-2.5 w-20 rounded-full bg-border-strong sm:block" />
            <span className="landing-click relative inline-flex h-8 items-center rounded-[8px] bg-surface-1 px-3 text-[12.5px] font-semibold whitespace-nowrap shadow-[0_0_0_2px_var(--accent)]">
              {t.demoButton}
              <span className="absolute -right-1.5 -bottom-1.5 size-4 rounded-full border-2 border-surface-1 bg-accent" />
            </span>
          </div>
          <Lines widths={[84, 66, 76, 58, 70]} />
        </div>
        <div className="grid content-start gap-2 border-l border-border p-3">
          <p className="flex items-center gap-1.5 text-[12px] font-semibold text-critical">
            <span className="size-2 rounded-full bg-critical" />
            {t.demoRecording}
          </p>
          {t.demoSteps.map((s, i) => (
            <div
              key={s}
              className={`flex gap-2 rounded-[8px] p-1.5 text-[12px] ${
                i === 1 ? 'landing-step-in bg-accent-soft font-medium' : 'text-text-2'
              }`}
            >
              <span
                className={`flex size-[18px] shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                  i === 1 ? 'bg-accent text-on-accent' : 'bg-surface-2 text-text-2'
                }`}
              >
                {i + 1}
              </span>
              <span className="grid min-w-0 flex-1 gap-1">
                {s}
                <span className="h-8 rounded-[6px] bg-surface-2 shadow-[inset_0_0_0_1px_var(--border)]" />
              </span>
            </div>
          ))}
          <span className="mt-1 inline-flex h-8 items-center justify-center rounded-full bg-accent text-[12px] font-semibold text-on-accent">
            {t.demoFinish}
          </span>
        </div>
      </div>
    </figure>
  );
}

/** Um guia de exemplo, desenhado em HTML (acompanha o tema claro/escuro). */
function GuidePreview({ t }: { t: LandingText }) {
  const steps = t.previewSteps;
  return (
    <figure
      aria-label={t.previewLabel}
      className="overflow-hidden rounded-[20px] bg-surface-1 shadow-[inset_0_0_0_1px_var(--border),0_24px_48px_-24px_rgb(0_0_0/0.25)]"
    >
      <WindowBar>{t.previewBar}</WindowBar>
      <div className="grid gap-0 md:grid-cols-[minmax(0,200px)_minmax(0,1fr)]">
        <ol className="grid content-start gap-1 border-b border-border p-3 md:border-r md:border-b-0">
          {steps.map((s, i) => (
            <li
              key={s}
              className={`flex items-start gap-2.5 rounded-[10px] px-2.5 py-2 text-[13.5px] ${
                i === 1 ? 'bg-accent-soft font-medium text-text-1' : 'text-text-2'
              }`}
            >
              <span
                className={`mt-px flex size-5 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${
                  i === 1 ? 'bg-accent text-on-accent' : 'bg-surface-2 text-text-2'
                }`}
              >
                {i + 1}
              </span>
              {s}
            </li>
          ))}
        </ol>
        <div className="grid gap-3 p-4">
          <p className="text-[13px] font-medium text-text-3">{t.previewStepOf}</p>
          <p className="text-[17px] font-semibold tracking-tight">{steps[1]}</p>
          {/* A captura: uma aplicação esboçada, com o botão destacado */}
          <div className="relative overflow-hidden rounded-[12px] bg-surface-0 p-3 shadow-[inset_0_0_0_1px_var(--border)]">
            <div className="flex items-center justify-between">
              <span className="h-2.5 w-24 rounded-full bg-border-strong" />
              <span className="relative inline-flex h-8 items-center rounded-[8px] bg-surface-1 px-3 text-[12.5px] font-semibold shadow-[0_0_0_2px_var(--accent)]">
                {t.previewButton}
                <span className="absolute -right-1.5 -bottom-1.5 size-4 rounded-full border-2 border-surface-1 bg-accent" />
              </span>
            </div>
            <div className="mt-3 grid gap-2">
              {[88, 72, 80, 64].map((w) => (
                <div key={w} className="flex items-center gap-3">
                  <span className="h-2 w-16 rounded-full bg-border" />
                  <span className="h-2 rounded-full bg-border" style={{ width: `${w / 2}%` }} />
                </div>
              ))}
            </div>
          </div>
          <p className="rounded-[10px] bg-surface-2 px-3 py-2 text-[13px] text-text-2">
            {t.previewTip}
          </p>
        </div>
      </div>
    </figure>
  );
}

/** «Guiar»: a aplicação com o campo realçado e a indicação da extensão ao lado. */
function GuideVisual({ t }: { t: LandingText }) {
  return (
    <div aria-hidden className={`overflow-hidden ${card}`}>
      <WindowBar>{t.demoHost}</WindowBar>
      <div className="grid gap-5 bg-surface-0 p-5 sm:grid-cols-[auto_minmax(0,1fr)] sm:items-center">
        <div className="grid gap-4">
          <span className="h-2.5 w-28 rounded-full bg-border-strong" />
          <span className="inline-flex h-9 w-40 items-center justify-between rounded-[8px] bg-surface-1 px-3 text-[13px] font-semibold shadow-[0_0_0_2px_var(--accent)]">
            {t.guideField}
            <span className="text-text-3">▾</span>
          </span>
          <Lines widths={[90, 70]} />
        </div>
        <div className="relative rounded-[12px] bg-ink p-3.5 text-[13px] text-surface-0 sm:before:absolute sm:before:top-1/2 sm:before:-left-1.5 sm:before:size-3 sm:before:-translate-y-1/2 sm:before:rotate-45 sm:before:bg-ink">
          <p className="text-[11.5px] opacity-70">{t.guideStepOf}</p>
          <p className="mt-1 font-medium">{t.guideHint}</p>
        </div>
      </div>
    </div>
  );
}

/** «Executar»: o painel da execução a pedir confirmação num botão que altera dados. */
function RunVisual({ t }: { t: LandingText }) {
  return (
    <div aria-hidden className={`overflow-hidden ${card}`}>
      <WindowBar>{t.demoHost}</WindowBar>
      <div className="grid gap-4 bg-surface-0 p-5">
        <div className="flex items-center justify-between gap-3">
          <Lines widths={[60]} />
          <span className="inline-flex h-8 shrink-0 items-center rounded-[8px] bg-surface-1 px-3 text-[12.5px] font-semibold shadow-[0_0_0_2px_var(--accent)]">
            {t.runButton}
          </span>
        </div>
        <div className="grid gap-2.5 rounded-[12px] bg-surface-1 p-3.5 shadow-[inset_0_0_0_1px_var(--border),0_12px_24px_-16px_rgb(0_0_0/0.3)]">
          <p className="text-[12.5px] font-semibold">{t.runTitle}</p>
          <p className="text-[12px] text-text-3">{t.runStepOf}</p>
          <p className="rounded-[8px] bg-attention-soft px-2.5 py-2 text-[12.5px] text-attention">
            {t.runAsk}
          </p>
          <div className="flex gap-2">
            <span className="inline-flex h-8 items-center rounded-full bg-accent px-3 text-[12px] font-semibold text-on-accent">
              {t.runConfirm}
            </span>
            <span className="inline-flex h-8 items-center rounded-full px-3 text-[12px] font-semibold shadow-[inset_0_0_0_1px_var(--border-strong)]">
              {t.runPause}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Ler / Guiar / Executar, em separadores (setas do teclado para mudar). */
function ModeTabs({ t }: { t: LandingText }) {
  const [active, setActive] = useState(0);
  const tabs = useRef<(HTMLButtonElement | null)[]>([]);
  const mode = t.modes[active]!;
  const go = (i: number) => {
    const n = (i + t.modes.length) % t.modes.length;
    setActive(n);
    tabs.current[n]?.focus();
  };
  return (
    <div className="grid gap-6">
      <div
        role="tablist"
        aria-label={t.modesTitle}
        className="inline-flex w-fit gap-1 rounded-full bg-surface-2 p-1"
        onKeyDown={(e) => {
          if (e.key === 'ArrowRight') go(active + 1);
          else if (e.key === 'ArrowLeft') go(active - 1);
          else return;
          e.preventDefault();
        }}
      >
        {t.modes.map((m, i) => (
          <button
            key={m.tab}
            ref={(el) => {
              tabs.current[i] = el;
            }}
            type="button"
            role="tab"
            id={`modo-${i}`}
            aria-selected={i === active}
            aria-controls="modo-painel"
            tabIndex={i === active ? 0 : -1}
            onClick={() => setActive(i)}
            className={`inline-flex h-9 items-center gap-1.5 rounded-full px-4 text-[14px] font-semibold ${
              i === active
                ? 'bg-surface-1 text-text-1 shadow-[0_1px_3px_rgb(0_0_0/0.12)]'
                : 'text-text-2 hover:text-text-1'
            }`}
          >
            <span aria-hidden>{landingIcons.modes[i]}</span>
            {m.tab}
          </button>
        ))}
      </div>
      <div
        role="tabpanel"
        id="modo-painel"
        aria-labelledby={`modo-${active}`}
        className="grid items-center gap-8 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
      >
        <div>
          <h3 className="text-xl font-semibold tracking-tight">{mode.title}</h3>
          <ul className="mt-4 grid gap-2.5 text-[15px] text-text-2">
            {mode.points.map((p) => (
              <li key={p} className="flex gap-2.5">
                <span aria-hidden className="font-semibold text-accent-text">
                  →
                </span>
                {p}
              </li>
            ))}
          </ul>
          {mode.note && <p className="mt-4 text-[13px] text-text-3">{mode.note}</p>}
        </div>
        {active === 0 ? (
          <GuidePreview t={t} />
        ) : active === 1 ? (
          <GuideVisual t={t} />
        ) : (
          <RunVisual t={t} />
        )}
      </div>
    </div>
  );
}

/** Imagens das três funcionalidades em destaque: capturas, dados pessoais, exportações. */
function SpotlightVisual({ i, t }: { i: number; t: LandingText }) {
  const chip = 'rounded-[8px] bg-surface-2 px-2.5 py-1.5 text-[12px] font-semibold';
  if (i === 0)
    return (
      <div className="grid gap-5">
        <div className="flex items-center justify-between gap-3">
          <Lines widths={[55]} />
          <span className="inline-flex h-8 shrink-0 items-center rounded-[8px] bg-surface-0 px-3 text-[12.5px] font-semibold shadow-[0_0_0_2px_var(--accent)]">
            {t.shotButton}
          </span>
        </div>
        <Lines widths={[86, 64, 74]} />
        <div className="flex flex-wrap gap-2">
          {t.editorTools.map((x) => (
            <span key={x} className={chip}>
              {x}
            </span>
          ))}
        </div>
      </div>
    );
  if (i === 1) {
    const blur =
      'inline-block h-3 rounded-[4px] bg-[repeating-linear-gradient(90deg,var(--border-strong)_0_4px,var(--border)_4px_8px)] align-middle blur-[1.2px]';
    return (
      <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-4 gap-y-3.5 text-[13.5px]">
        <dt className="text-text-3">{t.pii.customer}</dt>
        <dd>{t.pii.customerValue}</dd>
        <dt className="text-text-3">{t.pii.taxId}</dt>
        <dd className="flex items-center gap-2">
          <span className={`${blur} w-28`} />
          <span className="rounded-full bg-accent-soft px-2 py-0.5 text-[11.5px] font-semibold text-accent-text">
            {t.pii.hidden}
          </span>
        </dd>
        <dt className="text-text-3">{t.pii.iban}</dt>
        <dd>
          <span className={`${blur} w-44 max-w-full`} />
        </dd>
        <dt className="text-text-3">{t.pii.password}</dt>
        <dd>••••••••</dd>
      </dl>
    );
  }
  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap gap-2">
        {t.exportFormats.map((x) => (
          <span key={x} className={chip}>
            {x}
          </span>
        ))}
      </div>
      <div className="grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)] gap-2.5">
        <span className="aspect-[16/10] rounded-[8px] bg-accent" />
        <span className="aspect-[16/10] rounded-[8px] bg-surface-0 shadow-[inset_0_0_0_1px_var(--border)]" />
        <span className="aspect-[16/10] rounded-[8px] bg-surface-0 shadow-[inset_0_0_0_1px_var(--border)]" />
      </div>
    </div>
  );
}

export function LandingPage() {
  const { session } = useSession();
  const locale = useLocale();
  const t = landingText(locale);
  const to = publicPaths(locale);
  useEffect(() => {
    const before = document.title;
    document.title = t.docTitle;
    return () => {
      document.title = before;
    };
  }, [t.docTitle]);

  const navLink = 'rounded-full px-3 py-1.5 text-[14px] font-medium text-text-2 hover:text-text-1';

  return (
    <div className="flex min-h-dvh flex-col" data-landing>
      <header className="sticky top-0 z-20 border-b border-border bg-surface-0/85 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-6xl items-center gap-1 px-4 sm:px-6">
          <Link to={to.product} aria-label={t.home}>
            <Logo />
          </Link>
          <span className="flex-1" />
          <nav aria-label={t.nav} className="hidden items-center lg:flex">
            <a href="#como" className={navLink}>
              {t.navHow}
            </a>
            <a href="#usar" className={navLink}>
              {t.navModes}
            </a>
            <a href="#para-quem" className={navLink}>
              {t.navUses}
            </a>
          </nav>
          <Link to={to.pricing} className={`hidden sm:inline-flex ${navLink}`}>
            {t.pricing}
          </Link>
          {session ? (
            <Link
              to="/inicio"
              className="ml-1 inline-flex h-9 items-center rounded-full bg-accent px-4 text-[14px] font-semibold text-on-accent hover:brightness-95"
            >
              {t.openApp}
            </Link>
          ) : (
            <>
              <Link to="/entrar" className={navLink}>
                {t.signIn}
              </Link>
              <Link
                to="/entrar"
                className="ml-1 inline-flex h-9 items-center rounded-full bg-accent px-4 text-[14px] font-semibold text-on-accent hover:brightness-95"
              >
                {t.startFree}
              </Link>
            </>
          )}
        </div>
      </header>

      <main id="main" className="flex-1">
        {/* Abertura: o texto e a extensão a gravar */}
        <section className="mx-auto grid max-w-6xl items-center gap-10 px-4 pt-14 pb-14 sm:px-6 sm:pt-20 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]">
          <div className="grid gap-5">
            <p className="text-[14px] font-medium text-accent-text">{t.eyebrow}</p>
            <h1 className="text-4xl leading-[1.08] font-semibold tracking-tight sm:text-5xl">
              {t.heroA}
              <br />
              <span className="text-text-2">{t.heroB}</span>
            </h1>
            <p className="max-w-xl text-lg text-text-2">{t.heroText}</p>
            <div className="flex flex-wrap gap-3">
              <Cta to="/entrar" primary>
                {t.startFree} →
              </Cta>
              <Cta to={to.pricing}>{t.seePlans}</Cta>
            </div>
            <ul className="flex flex-wrap gap-x-5 gap-y-1 text-[13.5px] text-text-2">
              {t.heroTicks.map((x) => (
                <li key={x} className="flex gap-1.5">
                  <span aria-hidden className="font-semibold text-positive">
                    ✓
                  </span>
                  {x}
                </li>
              ))}
            </ul>
          </div>
          <HeroDemo t={t} />
        </section>

        {/* Em resumo: factos do produto */}
        <section aria-label={t.factsLabel} className="border-y border-border bg-surface-1">
          <dl className="mx-auto grid max-w-6xl grid-cols-2 lg:grid-cols-4">
            {t.facts.map((f, i) => (
              <div
                key={f.title}
                className={`grid gap-0.5 px-4 py-5 sm:px-6 ${i % 2 ? '' : 'border-r border-border'} ${
                  i < 2 ? 'border-b border-border lg:border-b-0' : ''
                } ${i === 1 ? 'lg:border-r' : ''}`}
              >
                <dt className="text-[15px] font-semibold">{f.title}</dt>
                <dd className="text-[13px] text-text-3">{f.text}</dd>
              </div>
            ))}
          </dl>
        </section>

        {/* Como funciona: à mão ou gravado */}
        <section aria-labelledby="como" className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6">
          <div className="max-w-2xl">
            <h2
              id="como"
              className="scroll-mt-20 text-2xl font-semibold tracking-tight sm:text-3xl"
            >
              {t.howTitle}
            </h2>
            <p className="mt-2 text-text-2">{t.howText}</p>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className={`grid content-between gap-6 p-6 ${card}`}>
              <div>
                <h3 className="text-lg font-semibold">{t.byHand}</h3>
                <ol className="mt-3 grid list-decimal gap-1.5 pl-5 text-[15px] text-text-2">
                  {t.byHandSteps.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ol>
              </div>
              <p className="text-2xl font-semibold tracking-tight text-text-3">{t.byHandResult}</p>
            </div>
            <div
              className={`grid content-between gap-6 p-6 ${card} shadow-[inset_0_0_0_2px_var(--accent)]`}
            >
              <div>
                <h3 className="text-lg font-semibold text-accent-text">{t.withUs}</h3>
                <ol className="mt-3 grid list-decimal gap-1.5 pl-5 text-[15px] text-text-2">
                  {t.withUsSteps.map((s) => (
                    <li key={s}>{s}</li>
                  ))}
                </ol>
              </div>
              <div>
                <p className="text-2xl font-semibold tracking-tight">{t.withUsResult}</p>
                <p className="mt-1 text-[13px] text-text-3">{t.estimate}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Três formas de usar um guia */}
        <section aria-labelledby="usar" className="border-y border-border bg-surface-1">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6">
            <div className="max-w-2xl">
              <h2
                id="usar"
                className="scroll-mt-20 text-2xl font-semibold tracking-tight sm:text-3xl"
              >
                {t.modesTitle}
              </h2>
              <p className="mt-2 text-text-2">{t.modesText}</p>
            </div>
            <ModeTabs t={t} />
          </div>
        </section>

        {/* Funcionalidades: três em destaque, as outras em cartões */}
        <section
          aria-labelledby="funcionalidades"
          className="mx-auto grid max-w-6xl gap-4 px-4 py-16 sm:px-6"
        >
          <h2 id="funcionalidades" className="text-2xl font-semibold tracking-tight sm:text-3xl">
            {t.featuresTitle}
          </h2>
          <div className="grid divide-y divide-dashed divide-border">
            {t.spotlights.map((s, i) => (
              <div key={s.title} className="grid items-center gap-8 py-10 md:grid-cols-2">
                <div className={i % 2 ? 'md:order-2' : ''}>
                  <span className="rounded-full bg-accent-soft px-2.5 py-1 text-[12px] font-semibold text-accent-text">
                    {s.tag}
                  </span>
                  <h3 className="mt-3 text-xl font-semibold tracking-tight">{s.title}</h3>
                  <p className="mt-2 text-text-2">{s.text}</p>
                </div>
                <div aria-hidden className={`min-w-0 p-6 ${card}`}>
                  <SpotlightVisual i={i} t={t} />
                </div>
              </div>
            ))}
          </div>
          <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {t.features.map((f, i) => (
              <li key={f.title} className={`grid grid-cols-[auto_minmax(0,1fr)] gap-3 p-4 ${card}`}>
                <span
                  aria-hidden
                  className="flex size-9 items-center justify-center rounded-[10px] bg-accent-soft text-[17px]"
                >
                  {landingIcons.features[i]}
                </span>
                <span>
                  <h3 className="font-semibold">{f.title}</h3>
                  <p className="mt-0.5 text-[14px] text-text-2">{f.text}</p>
                </span>
              </li>
            ))}
          </ul>
        </section>

        {/* Para quem */}
        <section aria-labelledby="para-quem" className="border-y border-border bg-surface-1">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6">
            <div className="max-w-2xl">
              <h2
                id="para-quem"
                className="scroll-mt-20 text-2xl font-semibold tracking-tight sm:text-3xl"
              >
                {t.usesTitle}
              </h2>
              <p className="mt-2 text-text-2">{t.usesText}</p>
            </div>
            <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {t.uses.map((u) => (
                <li
                  key={u.title}
                  className="grid content-between gap-4 rounded-[14px] bg-surface-0 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
                >
                  <div>
                    <h3 className="font-semibold">{u.title}</h3>
                    <p className="mt-1 text-[14px] text-text-2">{u.text}</p>
                  </div>
                  <p className="text-[13px] text-text-3 italic">{u.example}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Acesso antecipado */}
        <section className="mx-auto max-w-6xl px-4 pt-16 sm:px-6">
          <div className="grid items-center gap-6 rounded-[20px] bg-ink p-7 text-surface-0 sm:p-9 md:grid-cols-[minmax(0,1.4fr)_auto]">
            <div>
              <p className="text-[13px] font-semibold text-bright">{t.betaEyebrow}</p>
              <h2 className="mt-2 text-2xl font-semibold tracking-tight">{t.betaTitle}</h2>
              <p className="mt-2 max-w-2xl opacity-80">{t.betaText}</p>
            </div>
            <Link
              to={to.beta}
              data-early-access
              className="inline-flex h-11 w-fit items-center justify-center rounded-full bg-accent px-5 text-[15px] font-semibold text-on-accent hover:brightness-95"
            >
              {t.betaCta} →
            </Link>
          </div>
        </section>

        {/* Privacidade */}
        <section
          aria-labelledby="privacidade"
          className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6"
        >
          <div className="max-w-2xl">
            <h2 id="privacidade" className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.privacyTitle}
            </h2>
            <p className="mt-2 text-text-2">{t.privacyText}</p>
          </div>
          <ul className="grid gap-4 md:grid-cols-3">
            {t.security.map((line) => (
              <li key={line} className={`flex gap-3 p-4 text-[14px] ${card}`}>
                <span aria-hidden className="text-positive">
                  ✓
                </span>
                {line}
              </li>
            ))}
          </ul>
          <p className="text-[14px] text-text-2">
            {t.learnMore}{' '}
            <Link
              to={to.privacy}
              className="font-medium text-accent-text underline-offset-2 hover:underline"
            >
              {t.privacyPolicy}
            </Link>
            .
          </p>
        </section>

        {/* Planos */}
        <section aria-labelledby="planos" className="border-y border-border bg-surface-1">
          <div className="mx-auto grid max-w-6xl gap-8 px-4 py-16 sm:px-6">
            <h2 id="planos" className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.plansTitle}
            </h2>
            <ul className="grid gap-4 md:grid-cols-3">
              {t.plans.map((x) => (
                <li
                  key={x.n}
                  className="rounded-[14px] bg-surface-0 p-5 shadow-[inset_0_0_0_1px_var(--border)]"
                >
                  <h3 className="font-semibold">{x.n}</h3>
                  <p className="mt-1 text-[22px] font-semibold tracking-tight">{x.p}</p>
                  <p className="mt-1 text-[14px] text-text-2">{x.t}</p>
                </li>
              ))}
            </ul>
            <div>
              <Cta to={to.pricing}>{t.comparePlans}</Cta>
            </div>
          </div>
        </section>

        {/* Perguntas frequentes */}
        <section aria-labelledby="faq">
          <div className="mx-auto grid max-w-3xl gap-6 px-4 py-16 sm:px-6">
            <h2 id="faq" className="text-2xl font-semibold tracking-tight sm:text-3xl">
              {t.faqTitle}
            </h2>
            <div className={`grid divide-y divide-border ${card}`}>
              {t.faq.map((f) => (
                <details key={f.q} className="group px-4 py-3">
                  <summary className="flex cursor-pointer list-none items-center justify-between gap-3 font-medium [&::-webkit-details-marker]:hidden">
                    {f.q}
                    <span
                      aria-hidden
                      className="text-text-3 transition-transform group-open:rotate-90"
                    >
                      →
                    </span>
                  </summary>
                  <p className="mt-2 text-[14px] text-text-2">{f.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Fecho */}
        <section className="border-t border-border bg-surface-1">
          <div className="mx-auto grid max-w-6xl justify-items-center gap-4 px-4 py-16 text-center sm:px-6">
            <h2 className="text-2xl font-semibold tracking-tight sm:text-3xl">{t.closeTitle}</h2>
            <p className="max-w-xl text-text-2">{t.closeText}</p>
            <div className="flex flex-wrap justify-center gap-3">
              <Cta to="/entrar" primary>
                {t.startFree} →
              </Cta>
              <a
                href={mailto(t.demoSubject)}
                className="inline-flex h-11 items-center justify-center rounded-full px-5 text-[15px] font-semibold text-text-1 shadow-[inset_0_0_0_1px_var(--border-strong)] hover:bg-surface-2"
              >
                {t.askDemo}
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="border-t border-border">
        <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-8 text-[14px] sm:flex-row sm:items-center sm:px-6">
          <div className="grid gap-1">
            <Logo size={18} />
            <span className="text-[12.5px] text-text-3">{t.tagline}</span>
          </div>
          <span className="flex-1" />
          <nav aria-label={t.info} className="flex flex-wrap gap-x-4 gap-y-2 text-text-2">
            <Link to={to.pricing} className="hover:text-text-1">
              {t.pricing}
            </Link>
            <Link to={to.privacy} className="hover:text-text-1">
              {t.privacy}
            </Link>
            <Link to={to.terms} className="hover:text-text-1">
              {t.terms}
            </Link>
            <Link to={to.refunds} className="hover:text-text-1">
              {t.refunds}
            </Link>
            <a href={`mailto:${CONTACT_EMAIL}`} className="hover:text-text-1">
              {t.contact}
            </a>
          </nav>
          <span className="text-[12.5px] text-text-3">
            {t.madeBy}{' '}
            <a href="https://rdoria.com" className="hover:text-text-1">
              rdoria.com
            </a>
          </span>
        </div>
      </footer>
    </div>
  );
}
